// API layer for the Clock In/Out kiosk (/clock-in-out).
//
// The kiosk authenticates with an X-Token-Key of type ATTENDANCE_KIOSK,
// minted ONCE by POST /staffclockkiosk/enroll (manager bearer JWT) and kept
// in localStorage on the device. Staff identify themselves per action with
// { Staff_Id, Pin } (Staff.EposLoginPin) in the body. Every staffclock
// action returns the refreshed staffclock_state.
//
// A dedicated fetch wrapper is used (rather than helpers/APIdata/FetchAPI)
// so a wrong PIN / revoked kiosk token can never burn a logged-in manager's
// authUser session on the device.

import { ApiURL } from "../../../helpers/APIdata/apiURL";

const KIOSK_DEVICE_KEY = "crmit_staffclock_kiosk";

/* --------------------------- kiosk device store --------------------------- */

export const getKioskDevice = () => {
  try {
    const raw = localStorage.getItem(KIOSK_DEVICE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.token ? parsed : null;
  } catch {
    return null;
  }
};

export const saveKioskDevice = (device) => {
  localStorage.setItem(KIOSK_DEVICE_KEY, JSON.stringify(device));
  return device;
};

export const clearKioskDevice = () => {
  localStorage.removeItem(KIOSK_DEVICE_KEY);
};

export const newDeviceIdentifier = () =>
  `KIOSK-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

// Change #100094 - Settings > Clock-In Kiosks hands out a setup link of the
// form /clock-in-out#kiosk-token=<token>. The token rides in the URL FRAGMENT so
// it is never sent to a server or written to an access log. Returns the token
// (or null) and strips the fragment from the address bar either way, so the
// token does not linger in the tablet's history.
const KIOSK_TOKEN_HASH_KEY = "kiosk-token";

// Change #100098 - short code for a kiosk, shown in the tablet's corner and
// under the kiosk's name in Settings, so support can tell which one a caller
// is looking at. Built from StaffClockKiosk.Id, which is unique per company.
export const kioskShortCode = (staffClockKioskId) =>
  staffClockKioskId ? `K-${String(staffClockKioskId).padStart(4, "0")}` : "";

// The page itself, on this host. Change #100098: the settings screen prefers the
// company's own address (KioskUrl from devicelist) and only falls back to this
// until the backend carrying it is live.
export const kioskPageUrl = () => `${window.location.origin}/clock-in-out`;

export const kioskSetupLink = (token, kioskUrl) =>
  `${kioskUrl || kioskPageUrl()}#${KIOSK_TOKEN_HASH_KEY}=${encodeURIComponent(token)}`;

// Change #100098 - what a scanned QR code (or pasted text) says. The QR on the
// settings screen holds the whole setup link, so a phone camera can open it
// directly, while the kiosk's own scanner just wants the token out of it. A
// bare token is accepted too. Returns null for anything else, so scanning a
// stray QR code (a menu, a wifi sticker) is ignored rather than saved.
export const kioskTokenFromText = (text) => {
  const value = String(text || "").trim();
  if (!value) return null;
  const hashAt = value.indexOf("#");
  if (hashAt !== -1) {
    const token = new URLSearchParams(value.slice(hashAt + 1)).get(KIOSK_TOKEN_HASH_KEY);
    return token ? token.trim() || null : null;
  }
  return /^[A-Za-z0-9._~-]{16,256}$/.test(value) ? value : null;
};

export const takeKioskTokenFromUrl = () => {
  try {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return null;
    const token = new URLSearchParams(hash).get(KIOSK_TOKEN_HASH_KEY);
    if (!token) return null;
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    return token.trim() || null;
  } catch {
    return null;
  }
};

/* ------------------------------ fetch wrapper ----------------------------- */

const request = async (path, { method = "GET", body, kioskToken } = {}) => {
  let authUser = null;
  try {
    authUser = JSON.parse(localStorage.getItem("authUser"));
  } catch {
    /* no session */
  }

  try {
    const response = await fetch(`${ApiURL().API_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(kioskToken
          ? { "x-token-key": kioskToken }
          : authUser?.token
          ? { Authorization: `Bearer ${authUser.token}` }
          : {}),
      },
      body: body ? JSON.stringify(body) : null,
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      /* empty / non-JSON body */
    }

    const bodyStatus = typeof data?.status === "number" ? data.status : null;
    const ok =
      response.ok && (bodyStatus === null || (bodyStatus >= 200 && bodyStatus < 300));

    return {
      ok,
      data,
      message:
        data?.message ||
        data?.messages?.error ||
        data?.error ||
        (!ok ? `Request failed (${bodyStatus ?? response.status})` : ""),
    };
  } catch (error) {
    return { ok: false, data: null, message: error?.message || "Network error" };
  }
};

/* -------------------------------- endpoints ------------------------------- */

export const kioskStaffList = (kioskToken) =>
  request("/staffclockkiosk/stafflist", { kioskToken });

// Change #100101 - the tablet reports its device details for the Debug tab.
export const kioskHeartbeat = (kioskToken, deviceInfo) =>
  request("/staffclockkiosk/heartbeat", {
    method: "POST",
    body: { DeviceInfo: deviceInfo },
    kioskToken,
  });

export const kioskJobList = (kioskToken) =>
  request("/staffclockkiosk/joblist", { kioskToken });

// Manager-only (bearer JWT). Returns the kiosk token ONCE.
// Change #100097: optional CompanySiteLocation_Id, only sent when chosen so an
// older backend never sees the key.
export const kioskEnroll = (label, deviceIdentifier, companySiteLocationId) =>
  request("/staffclockkiosk/enroll", {
    method: "POST",
    body: {
      Label: label,
      DeviceIdentifier: deviceIdentifier,
      ...(companySiteLocationId ? { CompanySiteLocation_Id: Number(companySiteLocationId) } : {}),
    },
  });

// Manager-only (bearer JWT). Change #100098 - an existing kiosk's token again,
// for its setup QR / link.
export const kioskDeviceSetup = (staffClockKioskId) =>
  request(`/staffclockkiosk/devicesetup/${staffClockKioskId}`);

// Manager-only (bearer JWT). Change #100097 - rename a kiosk or change its
// location. fields: { Label?, CompanySiteLocation_Id? } (null clears the location).
export const kioskDeviceUpdate = (staffClockKioskId, fields) =>
  request("/staffclockkiosk/deviceupdate", {
    method: "PUT",
    body: { StaffClockKiosk_Id: staffClockKioskId, ...fields },
  });

// Manager-only (bearer JWT). Enrolled kiosks - never includes the token itself.
export const kioskDeviceList = () => request("/staffclockkiosk/devicelist");

// Manager-only (bearer JWT). Revokes the kiosk's token so the device stops working.
export const kioskDeviceDelete = (staffClockKioskId) =>
  request(`/staffclockkiosk/devicedelete/${staffClockKioskId}`, { method: "DELETE" });

// action: "status" | "clockin" | "clockout" | "job/clockin" | "job/clockout" | "job/swap"
export const staffClockAction = (kioskToken, action, body) =>
  request(`/staffclock/${action}`, { method: "POST", body, kioskToken });

/* --------------------------- response extraction -------------------------- */

export const pickState = (data) =>
  data?.staffclock_state || data?.data?.staffclock_state || null;

// First array under one of the preferred keys, else first array anywhere.
export const pickList = (data, preferredKeys = []) => {
  for (const key of preferredKeys) {
    const v = data?.[key] ?? data?.data?.[key];
    if (Array.isArray(v)) return v;
  }
  const scan = (obj, depth = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const v of Object.values(obj)) if (Array.isArray(v)) return v;
    for (const v of Object.values(obj)) {
      const found = scan(v, depth + 1);
      if (found) return found;
    }
    return null;
  };
  return scan(data) || [];
};

// Enroll returns the kiosk token once — find the first token-ish string.
export const pickToken = (data) => {
  const scan = (obj, depth = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const [k, v] of Object.entries(obj)) {
      if (/token/i.test(k) && typeof v === "string" && v.length >= 16) return v;
    }
    for (const v of Object.values(obj)) {
      const found = scan(v, depth + 1);
      if (found) return found;
    }
    return null;
  };
  return scan(data);
};

/* --------------------------------- helpers -------------------------------- */

export const flagOn = (v) => Number(v) === 1;

// Field names come back with varying prefixes (e.g. the open time entry's
// start is TimeEntry_TimeEntryStartDateTime) — find the first key matching.
export const pickField = (obj, regex) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const [k, v] of Object.entries(obj)) {
    if (regex.test(k) && v !== null && v !== undefined && v !== "") return v;
  }
  return undefined;
};

// "2026-07-16 08:58:00" (API) -> Date
export const parseApiDate = (value) => {
  if (!value) return null;
  const d = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const fmtTime = (value) => {
  const d = value instanceof Date ? value : parseApiDate(value);
  return d
    ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    : "";
};
